import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSystemStatus } from '../api/system-configuration/use-system-configuration';
import TradingSidebar from '../components/TradingSidebar.tsx';
import MaintenancePage from '../components/MaintenancePage.tsx';
import TraderBackground from '../components/trader/TraderBackground.tsx';
import MobileTraderHeader from '../components/mobile/MobileTraderHeader.tsx';
import { usePriceSocket } from '../hooks/usePriceSocket';
import { useTradesSocket } from '../hooks/useTradesSocket';
import { useCrmShellAttribute, useSidebarCollapsed } from './crm/use-crm-shell';
import { cn } from '@/lib/utils';

const TradingLayout: React.FC = () => {
    const location = useLocation();
    const { t } = useTranslation('common');
    const { isAuthenticated, isLoading, user, isTrader } = useAuth();

    usePriceSocket();
    useTradesSocket(user?.id);
    const { data: systemStatus, isLoading: isSystemStatusLoading } = useSystemStatus();

    // Initialize mobile detection synchronously to avoid flash
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth < 1024;
        }
        return false;
    });

    // Desktop only: the mobile view keeps its own bottom-nav look.
    useCrmShellAttribute(!isMobile, 'trader');
    const { collapsed, canToggle, toggle } = useSidebarCollapsed();

    // Update mobile detection on resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        window.addEventListener('resize', checkMobile);
        window.addEventListener('orientationchange', checkMobile);
        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('orientationchange', checkMobile);
        };
    }, []);

    if (isLoading || isSystemStatusLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-background text-foreground">
                <span>{t('loading')}</span>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }

    // Only allow traders (customers) to access this layout
    if (!user || !isTrader()) {
        return <Navigate to="/auth/login" replace />;
    }

    // Show maintenance page if system is in maintenance mode
    if (systemStatus?.maintenance_mode === true) {
        return <MaintenancePage />;
    }

    // Check if we're on the trade page to hide sidebar
    const isOnTradePage = location.pathname === '/trade';

    // Render mobile or desktop layout based on screen size
    if (isMobile) {
        return (
            <div className="trader-theme relative min-h-screen lg:hidden">
                <TraderBackground />
                <div className="relative z-10">
                    <MobileTraderHeader />
                    <Outlet />
                </div>
            </div>
        );
    }

    // Desktop: tenant-branded backdrop, floating glass sidebar and a content
    // container, as in the CRM shell. The trade terminal stays full-bleed and
    // hides the sidebar.
    return (
        <div className="trader-theme crm-backdrop relative hidden h-screen overflow-hidden text-foreground supports-[height:100dvh]:h-[100dvh] lg:flex">
            {!isOnTradePage && (
                <TradingSidebar
                    collapsed={collapsed}
                    canToggle={canToggle}
                    onToggleCollapsed={toggle}
                />
            )}
            <main
                id="main-content"
                className={cn(
                    'min-h-0 min-w-0 flex-1 overflow-auto',
                    !isOnTradePage &&
                        'me-2 my-2 rounded-[calc(var(--radius)+6px)] border border-content-border bg-app'
                )}
            >
                <Outlet />
            </main>
        </div>
    );
};

export default TradingLayout;
